import { FC, memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import bombeirosIcon from 'Static/images/bombeirosicon.jpeg';
import coordlyLogo from 'Static/images/logo-coordly-collapsed.png';
import { Icon } from 'Components/UI';

import styles from './Chat.module.scss';
import { EmptyState } from './EmptyState';
import { Messages } from './Messages';
import { useGetMessages, useSendMessageToThread, useSendNewThreadChat } from './hooks';
import { AIMessage } from './Messages/Message';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import planoDiretorGoianiaIcon from "Static/images/plano-diretor-goiania.png"

const ASSISTANT_OPTIONS = [
  { id: 'default', label: 'Assistente Coordia', assistant: undefined as string | undefined, image: coordlyLogo },
  { id: 'bombeiros', label: 'CBM-GO, Corpo de Bombeiros', assistant: 'Bombeiros' as string | undefined, image: bombeirosIcon },
  { id: 'prefeitura-goiania', label: 'Prefeitura de Goiânia', assistant: 'PrefeituraGoiania' as string | undefined, image: planoDiretorGoianiaIcon },
];

const ChatComponent: FC<{
  constructionSiteId: number;
  selectedThreadId?: string;
  setSelectedThreadId: (threadId: string | undefined) => void;
  assistant?: string;
  onViewAll?: () => void;
}> = ({ constructionSiteId, selectedThreadId, setSelectedThreadId, assistant, onViewAll }) => {
  const [value, setValue] = useState('');
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [chatAssistant, setChatAssistant] = useState<string | undefined>(undefined);
  const [assistantExplicitlyChosen, setAssistantExplicitlyChosen] = useState(false);
  const [showAssistantDropdown, setShowAssistantDropdown] = useState(false);
  const [selectorHidden, setSelectorHidden] = useState(false);
  const [assistantSearch, setAssistantSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { mutateAsync, isPending } = useSendNewThreadChat(constructionSiteId, chatAssistant ?? assistant);
  const {
    mutate: mutateSendMessageToThread,
    isPending: isLoadingSendMessageToThread
  } = useSendMessageToThread(constructionSiteId, selectedThreadId, (chunk) => {
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage && lastMessage.messageId === streamingMessageId) {
        return [
          ...prevMessages.slice(0, -1),
          { ...lastMessage, content: lastMessage.content + chunk }
        ];
      }
      return prevMessages;
    });
  }, chatAssistant ?? assistant);
  const {
    data: messagesFromThread,
    isLoading: isLoadingMessagesFromThread
  } = useGetMessages(constructionSiteId, selectedThreadId);

  useEffect(() => {
    if (selectedThreadId === undefined) {
      setChatAssistant(undefined);
      setAssistantExplicitlyChosen(false);
      setSelectorHidden(false);
      setShowAssistantDropdown(false);
    }
  }, [selectedThreadId]);

  useEffect(() => {
    if (assistant) {
      setChatAssistant(assistant);
      setAssistantExplicitlyChosen(true);
      setSelectorHidden(false);
    } else {
      setChatAssistant(undefined);
      setAssistantExplicitlyChosen(false);
    }
  }, [assistant]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowAssistantDropdown(false);
        setAssistantSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showAssistantSelector = !selectedThreadId || assistantExplicitlyChosen;

  const onSubmit = async () => {
    if (!value) return;
    if (isPending) return;

    setValue('');

    if (selectedThreadId === undefined && selectedThreadId !== 'TEMP') {
      setMessages(() => [
        {
          role: 0,
          content: value,
          insertedFrom: 'user',
          threadId: 'TEMP',
          messageId: 'TEMP'
        }
      ]);

      try {
        const { threadId, messageId, response } = await mutateAsync(value);
        setSelectedThreadId(threadId);
        setMessages((messages) => [
          ...messages,
          { role: 1, content: response, insertedFrom: 'user', threadId, messageId }
        ]);
      } catch (error) {
        setMessages((messages) => [
          ...messages,
          {
            role: 1,
            content: 'Erro ao carregar a resposta',
            insertedFrom: 'api',
            threadId: '',
            messageId: ''
          }
        ]);
      }
    } else {
      const userMessageId = `user_${Date.now()}`;
      const aiMessageId = `ai_${Date.now()}`;

      setMessages((messages) => [
        ...messages,
        {
          role: 0,
          content: value,
          insertedFrom: 'user',
          threadId: selectedThreadId,
          messageId: userMessageId
        }
      ]);

      setStreamingMessageId(aiMessageId);
      setMessages((messages) => [
        ...messages,
        {
          role: 1,
          content: '',
          insertedFrom: 'user',
          threadId: selectedThreadId,
          messageId: aiMessageId
        }
      ]);

      mutateSendMessageToThread(value, {
        onSuccess: (response) => {
          setStreamingMessageId(null);
        },
        onError: (error) => {
          setStreamingMessageId(null);
          setMessages((messages) => {
            const updatedMessages = [...messages];
            const lastMessage = updatedMessages[updatedMessages.length - 1];

            if (lastMessage && lastMessage.messageId === aiMessageId && lastMessage.content) {
              return updatedMessages;
            }

            return [
              ...updatedMessages.slice(0, -1),
              {
                role: 1,
                content: 'Erro ao carregar a resposta',
                insertedFrom: 'api',
                threadId: selectedThreadId,
                messageId: aiMessageId
              }
            ];
          });
        }
      });
    }
  };

  useEffect(() => {
    if (messagesFromThread) {
      setMessages(
        messagesFromThread.messages
          .map((x) => ({ ...x, content: x.content, insertedFrom: 'api' }))
          .reverse() as AIMessage[]
      );
    }
  }, [messagesFromThread]);

  const onSelectPrompt = async (prompt: string) => {
    setValue('');
    setMessages(() => [
      {
        role: 0,
        content: prompt,
        insertedFrom: 'user',
        threadId: 'TEMP',
        messageId: 'TEMP'
      }
    ]);
    setSelectedThreadId('TEMP');

    try {
      const { threadId, response } = await mutateAsync(prompt);
      setSelectedThreadId(threadId);
      setMessages((messages) => [
        ...messages,
        {
          role: 1,
          content: response,
          insertedFrom: 'user',
          threadId: 'TEMP',
          messageId: 'TEMP'
        }
      ]);
    } catch (error) {
      setMessages((messages) => [
        ...messages,
        {
          role: 1,
          content: 'Erro ao carregar a resposta',
          insertedFrom: 'api',
          threadId: 'TEMP',
          messageId: 'TEMP'
        }
      ]);
    }
  };

  useLayoutEffect(() => {
    if (selectedThreadId && messages.length > 0) {
      setTimeout(() => {
        const chatContainer = window.document.getElementById('chat-container');
        chatContainer?.scrollTo(0, chatContainer?.scrollHeight);
      }, 200);
    }
  }, [selectedThreadId, messages]);

  return (
    <div className={styles['chatContainer']}>
      <div className={styles['innerContainer']}>
        {!selectedThreadId ? (
          <EmptyState onSelectPrompt={onSelectPrompt} />
        ) : (
          <Messages
            threadId={selectedThreadId}
            messagesFromThread={messages}
            isLoading={isLoadingMessagesFromThread}
          />
        )}
      </div>

      <div className={styles['footer']}>
        <div className={styles['sendContainer']}>
          <div className={styles['inputWrapper']}>
            <textarea
              className={styles['input']}
              placeholder="Digite uma pergunta sobre seus projetos ou tarefas..."
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={async (e) => {
                if (isPending || isLoadingSendMessageToThread) return;

                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  await onSubmit();
                }
              }}
              disabled={isPending || isLoadingSendMessageToThread}
            />
            {showAssistantSelector && (
              <div className={styles['assistantSelectorWrapper']} ref={dropdownRef}>
                {showAssistantDropdown && (
                  <div className={styles['assistantDropdown']}>
                    <div className={styles['assistantDropdownSearch']}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M21 21l-4.35-4.35" stroke="#8697A8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="11" cy="11" r="6" stroke="#8697A8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Pesquise um assistente"
                        value={assistantSearch}
                        onChange={(e) => setAssistantSearch(e.target.value)}
                        className={styles['assistantSearchInput']}
                      />
                    </div>
                    <div className={styles['assistantDropdownLabel']}>Assistentes</div>
                    {ASSISTANT_OPTIONS.filter(o =>
                      o.label.toLowerCase().includes(assistantSearch.toLowerCase())
                    ).map(option => (
                      <div
                        key={option.id}
                        className={`${styles['assistantOption']} ${chatAssistant === option.assistant && assistantExplicitlyChosen ? styles['assistantOptionActive'] : ''}`}
                        onClick={() => {
                          setChatAssistant(option.assistant);
                          setAssistantExplicitlyChosen(true);
                          setShowAssistantDropdown(false);
                          setAssistantSearch('');
                        }}
                      >
                        {option.image ? (
                          <img src={option.image} alt={option.label} className={styles['assistantOptionImage']} />
                        ) : (
                          <div className={styles['assistantOptionIconDefault']} />
                        )}
                        <span>{option.label}</span>
                      </div>
                    ))}
                    <button
                      type="button"
                      className={styles['viewAllButton']}
                      onClick={() => {
                        setShowAssistantDropdown(false);
                        setAssistantSearch('');
                        onViewAll?.();
                      }}
                    >
                      Ver todos
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className={`${styles['assistantSelectorButton']} ${assistantExplicitlyChosen ? styles['assistantSelectorButtonActive'] : ''}`}
                  onClick={() => {
                    if (!selectedThreadId) {
                      setShowAssistantDropdown(prev => !prev);
                    }
                  }}
                  disabled={!!selectedThreadId}
                >
                  {assistantExplicitlyChosen && (() => {
                    const selected = ASSISTANT_OPTIONS.find(o => o.assistant === chatAssistant);
                    return selected?.image ? (
                      <img src={selected.image} alt={selected.label} className={styles['assistantSelectorImage']} />
                    ) : null;
                  })()}
                  {!assistantExplicitlyChosen && (
                    <span className={styles['assistantSelectorIcon']}>
                      <Icon icon="assistentes" customSize={14} />
                    </span>
                  )}
                  <span className={styles['assistantSelectorLabel']}>
                    {assistantExplicitlyChosen
                      ? (ASSISTANT_OPTIONS.find(o => o.assistant === chatAssistant)?.label ?? 'Assistente Coordia')
                      : 'Assistentes'}
                  </span>
                  <svg
                    className={`${styles['chevronIcon']} ${showAssistantDropdown ? styles['chevronIconOpen'] : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            )}
            <button
              className={`${styles['sendButton']}`}
              onClick={
                !value || isPending || isLoadingSendMessageToThread ? undefined : onSubmit
              }
              disabled={!value || isPending || isLoadingSendMessageToThread}
            >
              {isPending || isLoadingSendMessageToThread ? (
                <Spin
                  indicator={
                    <LoadingOutlined
                      color="white"
                      style={{ fontSize: 12, color: 'white', marginRight: 6 }}
                    />
                  }
                />
              ) : (
                'Enviar'
              )}
            </button>
          </div>
        </div>
        <div className={styles['footerInfo']}>
          <span>
            Informações geradas por IA. Valide as informações antes de utilizá-las.
          </span>
        </div>
      </div>
    </div>
  );
};

ChatComponent.displayName = 'Chat';

export const Chat = memo(ChatComponent);
